import React, { useState, useEffect, useRef, useCallback } from "react";

interface SearchSuggestion {
  providerAddress: string;
  physicalCity: string;
  physicalState: string;
  physicalZip: string;
  country: string;
  county: string;
  providerName?: string;
  fullAddress: string;
  latitude?: number;
  longitude?: number;
}

interface AutocompletePrediction {
  placeId: string;
  text: string;
  mainText: string;
  secondaryText: string;
}

interface SearchAutosuggestProps {
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect: (suggestion: SearchSuggestion) => void;
  onManualLocationAdd?: () => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  maxLength?: number;
  disabled?: boolean;
  minSearchLength?: number;
  apiKey?: string;
  countryRestriction?: string;
  limit?: number;
  enableManualLocation?: boolean;
  sortingLocation?: string;
}

const SearchAutosuggest: React.FC<SearchAutosuggestProps> = ({
  value,
  onChange,
  onSuggestionSelect,
  onManualLocationAdd,
  placeholder = "Search address...",
  label = "Address",
  required = false,
  maxLength = 100,
  disabled = false,
  minSearchLength = 3,
  apiKey = process.env.REACT_APP_GOOGLE_PLACES_AUTOCOMPLETE_API_KEY,
  countryRestriction = "us",
  limit = 20,
  enableManualLocation = true,
  sortingLocation = "",
}) => {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const sortPredictions = (
    preds: AutocompletePrediction[],
  ): AutocompletePrediction[] => {
    if (sortingLocation === "Location") {
      return [...preds].sort((a, b) =>
        a.mainText.toLowerCase().localeCompare(b.mainText.toLowerCase()),
      );
    }
    return preds;
  };

  const searchAddress = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.length < minSearchLength) {
        setPredictions([]);
        setShowSuggestions(false);
        setSelectedIndex(-1);
        setHasSearched(false);
        return;
      }

      if (!apiKey) {
        console.warn("Google Places API key is not provided");
        return;
      }

      try {
        setIsSearching(true);
        setSelectedIndex(-1);
        setHasSearched(true);

        const response = await fetch(
          "https://places.googleapis.com/v1/places:autocomplete",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": apiKey,
              "X-Goog-FieldMask":
                "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
            },
            body: JSON.stringify({
              input: query,
              includedRegionCodes: [countryRestriction],
            }),
          },
        );

        if (!response.ok) {
          throw new Error(
            `Google Places Autocomplete error: ${response.status}`,
          );
        }

        const data = await response.json();
        const raw: any[] = data.suggestions || [];

        const formatted: AutocompletePrediction[] = raw
          .slice(0, limit)
          .map((s) => ({
            placeId: s.placePrediction?.placeId || "",
            text: s.placePrediction?.text?.text || "",
            mainText:
              s.placePrediction?.structuredFormat?.mainText?.text ||
              s.placePrediction?.text?.text ||
              "",
            secondaryText:
              s.placePrediction?.structuredFormat?.secondaryText?.text || "",
          }))
          .filter((p) => p.placeId && p.text);

        setPredictions(sortPredictions(formatted));
        setShowSuggestions(true);
      } catch (error) {
        console.error("Error searching address suggestions:", error);
        setPredictions([]);
        setShowSuggestions(true);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    [apiKey, countryRestriction, limit, minSearchLength, sortingLocation],
  );

  const fetchPlaceDetails = async (
    placeId: string,
  ): Promise<SearchSuggestion | null> => {
    if (!apiKey) return null;

    try {
      setIsFetchingDetails(true);

      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          headers: {
            "X-Goog-Api-Key": apiKey,
            "X-Goog-FieldMask":
              "addressComponents,location,formattedAddress,displayName",
          },
        },
      );

      if (!response.ok) return null;

      const details = await response.json();
      const components: any[] = details.addressComponents || [];

      const getComponent = (
        type: string,
        nameType: "longText" | "shortText" = "longText",
      ): string => {
        const comp = components.find((c) => c.types?.includes(type));
        return comp ? comp[nameType] || "" : "";
      };

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");
      const streetAddress = streetNumber
        ? `${streetNumber} ${route}`.trim()
        : route;

      return {
        providerAddress:
          streetAddress || details.formattedAddress?.split(",")[0] || "",
        physicalCity:
          getComponent("locality") ||
          getComponent("administrative_area_level_3") ||
          getComponent("administrative_area_level_2"),
        physicalState: getComponent("administrative_area_level_1"),
        physicalZip: getComponent("postal_code"),
        country: getComponent("country"),
        county: getComponent("administrative_area_level_2"),
        providerName: details.displayName?.text || streetAddress || "",
        fullAddress: details.formattedAddress || "",
        latitude: details.location?.latitude,
        longitude: details.location?.longitude,
      };
    } catch (error) {
      console.error("Error fetching place details:", error);
      return null;
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (newValue.length >= minSearchLength) {
      searchAddress(newValue);
    } else {
      setPredictions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      setHasSearched(false);
    }
  };

  const handleSuggestionSelect = useCallback(
    async (prediction: AutocompletePrediction) => {
      onChange(prediction.mainText || prediction.text);
      setShowSuggestions(false);
      setPredictions([]);
      setSelectedIndex(-1);
      setHasSearched(false);

      const details = await fetchPlaceDetails(prediction.placeId);
      if (details && onSuggestionSelect) {
        onSuggestionSelect(details);
      }
    },
    [onChange, onSuggestionSelect, apiKey],
  );

  const handleManualLocationAdd = useCallback(() => {
    if (onManualLocationAdd) {
      onManualLocationAdd();
    }
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, [onManualLocationAdd]);

  const totalItems =
    predictions.length +
    (enableManualLocation && hasSearched && !isSearching ? 1 : 0);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;
    if (totalItems === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < predictions.length) {
            handleSuggestionSelect(predictions[selectedIndex]);
          } else if (
            enableManualLocation &&
            selectedIndex === predictions.length
          ) {
            handleManualLocationAdd();
          }
        } else if (predictions.length > 0) {
          handleSuggestionSelect(predictions[0]);
        }
        break;

      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;

      case "Tab":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const scrollable = suggestionsRef.current.querySelector(
        ".suggestions-scrollable",
      ) as HTMLElement;
      if (scrollable) {
        const el = scrollable.children[selectedIndex] as HTMLElement;
        if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [predictions]);

  const showManualLocation =
    enableManualLocation && hasSearched && !isSearching;
  const isLoading = isSearching || isFetchingDetails;

  return (
    <div className="form-floating searchable-dropdown position-relative">
      <input
        type="text"
        className="form-control"
        id={`floating-${label}`}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (value.length >= minSearchLength && predictions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={label}
        maxLength={maxLength}
        disabled={disabled || isFetchingDetails}
        ref={inputRef}
        autoComplete="off"
        role="combobox"
        aria-expanded={showSuggestions}
        aria-haspopup="listbox"
      />
      <label htmlFor={`floating-${label}`}>
        {label}
        {required && <span style={{ color: "red" }}>*</span>}
      </label>

      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="suggestion-dropdown position-absolute w-100 bg-white border mt-1 shadow-lg"
          style={{
            zIndex: 1000,
            borderRadius: "0.375rem",
            border: "1px solid #ced4da",
            top: "100%",
            left: 0,
            display: "flex",
            flexDirection: "column",
          }}
          role="listbox"
        >
          {isLoading ? (
            <div className="p-3 text-muted text-center">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              {isFetchingDetails
                ? "Loading address details..."
                : "Searching addresses..."}
            </div>
          ) : (
            <>
              <div
                className="suggestions-scrollable"
                style={{ maxHeight: "250px", overflowY: "auto", flex: "1" }}
              >
                {predictions.length > 0 ? (
                  predictions.map((prediction, index) => (
                    <div
                      key={prediction.placeId}
                      className={`suggestion-item p-3 border-bottom ${
                        index === selectedIndex
                          ? "suggestion-item-selected"
                          : ""
                      }`}
                      onClick={() => handleSuggestionSelect(prediction)}
                      role="option"
                      aria-selected={index === selectedIndex}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSuggestionSelect(prediction);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          index === selectedIndex ? "#e7f1ff" : "white",
                        borderLeft:
                          index === selectedIndex
                            ? "3px solid #0d6efd"
                            : "3px solid transparent",
                        outline: "none",
                        transition: "all 0.15s ease-in-out",
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onMouseLeave={() => setSelectedIndex(-1)}
                    >
                      <div className="fw-bold text-primary mb-1">
                        {prediction.mainText}
                      </div>
                      {prediction.secondaryText && (
                        <div className="text-dark small">
                          {prediction.secondaryText}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-muted text-center">
                    No locations/addresses found. Try a different search term.
                  </div>
                )}
              </div>

              {showManualLocation && (
                <div
                  className="manual-location-button border-top"
                  style={{ flexShrink: 0, borderTop: "2px solid #dee2e6" }}
                >
                  <div
                    className={`suggestion-item p-3 ${
                      selectedIndex === predictions.length
                        ? "suggestion-item-selected"
                        : ""
                    }`}
                    onClick={handleManualLocationAdd}
                    role="option"
                    aria-selected={selectedIndex === predictions.length}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleManualLocationAdd();
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedIndex === predictions.length
                          ? "#e7f1ff"
                          : "white",
                      borderLeft:
                        selectedIndex === predictions.length
                          ? "3px solid #0d6efd"
                          : "3px solid transparent",
                      outline: "none",
                      transition: "all 0.15s ease-in-out",
                    }}
                    onMouseEnter={() => setSelectedIndex(predictions.length)}
                    onMouseLeave={() => setSelectedIndex(-1)}
                  >
                    <div className="d-flex align-items-center justify-content-center text-primary">
                      <span className="material-icons mx-1 text-primary">
                        add
                      </span>
                      <span>Enter Location or Address Manually</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(null, args);
    };

    const callNow = immediate && !timeout;

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(null, args);
  };
}

export default SearchAutosuggest;
